import { useState, useEffect, useRef } from "react";
import { FiDownload, FiEdit2 } from "react-icons/fi";
import { Button, Dropdown } from "react-bootstrap";
import DocumentModal from "./DocumentModal";
import { exportDocument } from "./documentHelpers";
import localization from "services/localization";
import SpecificAPI from "services/SpecificApi";
import { ClipLoader } from "react-spinners";
import { useThemeStore } from "context/useThemeStore";
import ShadowDOM from "react-shadow";

const urlCache = {};
const urlMapping = {};
const DocumentMessages = ({ msg, onUpdate, loading }) => {
  const [showModal, setShowModal] = useState(false);
  // const [rawContent, setRawContent] = useState(
  //   msg.updated_response ?? msg.response,
  // );
  const [displayContent, setDisplayContent] = useState("");
  const [draftContent, setDraftContent] = useState(
    msg.updated_response ?? msg.response,
  );
  const [imageLoading, setImageLoading] = useState(false);
  const { theme } = useThemeStore();
  const iframeRef = useRef(null);

  const documentTitle = "AI Document";
  const BASE_API_URL = process.env.REACT_APP_API_URL;

  // Keep draftContent synced with msg updates
  useEffect(() => {
    if (iframeRef.current) {
      const doc =
        iframeRef.current.contentDocument ||
        iframeRef.current.contentWindow.document;
      const body = doc.body;
      if (body) body.setAttribute("data-theme", theme);
    }
  }, [theme]);

  // Replace resource URLs with signed URLs
  useEffect(() => {
    const replaceResourceUrls = async () => {
      var rawContent = msg.updated_response ?? msg.response;

      if (!rawContent) return;
      if (!rawContent.includes(`${BASE_API_URL}/api/resources_surl/`)) {
        setDisplayContent(rawContent);
        return;
      }

      setImageLoading(true);

      let updatedHtml = rawContent;

      const regex = new RegExp(
        `${BASE_API_URL.replace(/[.*+?^${}()|[\\]\\\\]/g, "\\$&")}/api/resources_surl/([^/]+)/([^'"\\s>]+)`,
        "g",
      );

      const urls = Array.from(rawContent.matchAll(regex));

      for (let match of urls) {
        const [fullUrl, schoolId, resourceId] = match;

        try {
          // Check cache first
          if (!urlCache[fullUrl]) {
            const data = await SpecificAPI.getResourceURL(resourceId, schoolId);
            if (data?.["signed-url"]) {
              urlCache[fullUrl] = data["signed-url"];
            } else {
              urlCache[fullUrl] = fullUrl;
            }
            urlMapping[fullUrl] = urlCache[fullUrl];
          }
          updatedHtml = updatedHtml.replace(fullUrl, urlCache[fullUrl]);
        } catch (err) {
          console.error("Failed to fetch signed URL for", fullUrl, err);
        }
      }
      debugger;

      setDisplayContent(updatedHtml);
      setImageLoading(false);
    };

    replaceResourceUrls();
  }, [msg.updated_response, msg.response]);

  // -------------------- DOWNLOAD --------------------
  const handleDownload = (type) => {
    exportDocument(displayContent, documentTitle, type);
  };

  const getThemedHTML = (html, theme) => {
    if (!html) return "";

    // Theme CSS for iframe
    const themeCSS = `
    <style>
      [data-theme="dark"] {
        --main-text-color: #f9fafb;
        --document-secondary-bg: #3b4465;
        --document-primary-text: #529ae3;
      }
      [data-theme="light"] {
        --main-text-color: #111827;
        --document-secondary-bg: #f9f9f9;
        --document-primary-text: #003366;
      }
      body {
        color: var(--main-text-color);
        font-family: 'Inter', sans-serif !important;
        font-size: 15px;
        line-height: 1.75;
      }
    </style>
  `;

    // Inject <body> if missing
    if (/<body[^>]*>/.test(html)) {
      return html.replace(
        /<body([^>]*)>/,
        `<body$1 data-theme="${theme}">${themeCSS}`,
      );
    } else {
      return `<body data-theme="${theme}">${themeCSS}${html}</body>`;
    }
  };

  return (
    <>
      {imageLoading ? (
        <div className="flex justify-center py-4">
          <ClipLoader size={30} color="var(--primary-icon)" />
        </div>
      ) : (
        <div className="border custom_card border-border rounded-xl shadow-sm overflow-hidden relative">
          {msg.is_editable && (
            <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
              <Dropdown align="end">
                <Dropdown.Toggle
                  size="sm"
                  as="button"
                  className="action-btn-outline secondary transition flex items-center justify-center"
                  style={{ border: "none" }}
                >
                  <FiDownload />
                </Dropdown.Toggle>

                <Dropdown.Menu
                  className="bg-card border border-border shadow-lg rounded-lg"
                  style={{ zIndex: 9999 }}
                >
                  <Dropdown.Item
                    onClick={() => handleDownload("pdf")}
                    className="custom-dropdown-item"
                  >
                    {localization.export_as_pdf || "Export as PDF"}
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => handleDownload("docx")}
                    className="custom-dropdown-item"
                  >
                    {localization.export_as_docx || "Export as DOCX"}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          )}
          {/* DOCUMENT CONTENT */}
          <ShadowDOM.div
            style={{
              padding: "1.5rem", // p-6
              maxHeight: "50vh",
              maxWidth: "80vw",
              overflow: "auto",
            }}
          >
            <div
              className="document-content main-text"
              dangerouslySetInnerHTML={{ __html: displayContent }}
            />
          </ShadowDOM.div>
          {/* <iframe
            ref={iframeRef}
            srcDoc={getThemedHTML(displayContent, theme)}
            style={{
              width: "77vw",
              height: "50vh",
              border: "none",
              overflow: "auto",
            }}
          /> */}

          {/* EDIT BUTTON */}
          {msg.is_editable && (
            <div className="p-4 flex justify-end">
              <Button
                size="sm"
                className="action-btn primary flex items-center gap-2"
                onClick={() => setShowModal(true)}
              >
                <FiEdit2 />
                {localization.edit || "Edit"}
              </Button>
            </div>
          )}
        </div>
      )}
      <DocumentModal
        msg={msg}
        onUpdate={onUpdate}
        loading={loading}
        setShowModal={setShowModal}
        showModal={showModal}
        draftContent={draftContent}
        displayContent={displayContent}
        urlMapping={urlMapping}
      />
    </>
  );
};

export default DocumentMessages;
